import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import { useTranslations } from 'use-intl';
import {
	FincluCustomerVehicleOffering,
	FincluCustomerQuestionnaire
} from '@/pages/CustomerSupport/types';
import { formatCurrency, formatDateTime } from '@/utils/utils';
import { useOfferingVehiclesStore } from '../useOfferingVehicles';
import { useCustomerVariablesStore } from '../useCustomerVariables';

interface VehicleOfferingSectionProps {
	vehicleOffering: FincluCustomerVehicleOffering | null;
	questionnaire: FincluCustomerQuestionnaire | null;
	customerCreditPayment: number | null;
	organizationId: string;
	isExpanded: boolean;
}

const VehicleOfferingSection: React.FC<VehicleOfferingSectionProps> = ({
	vehicleOffering,
	questionnaire,
	customerCreditPayment,
	organizationId,
	isExpanded: parentIsExpanded
}) => {
	const t = useTranslations('SupportClients');
	const [isExpanded, setIsExpanded] = useState(parentIsExpanded);
	const { vehicles, loadVehicles } = useOfferingVehiclesStore();
	const { variables, loadVariables } = useCustomerVariablesStore();

	useEffect(() => {
		setIsExpanded(parentIsExpanded);
	}, [parentIsExpanded]);

	useEffect(() => {
		if (organizationId) {
			loadVehicles(organizationId);
			loadVariables(organizationId);
		}
	}, [organizationId, loadVehicles, loadVariables]);

	const getVehicleOfferingLabel = (offering: string) => {
		const labels: Record<string, string> = {
			unable_to_pay: t('UnableToPay'),
			offer_low_end_vehicle: t('OfferLowEndVehicle'),
			offer_high_end_vehicle: t('OfferHighEndVehicle')
		};
		return labels[offering] || offering;
	};

	const getVehicleTypeLabel = (type: string) => {
		const labels: Record<string, string> = {
			fuel: t('VehicleTypeFuel'),
			electric: t('VehicleTypeElectric')
		};
		return labels[type] || type;
	};

	const getVehicleOfferingReasoning = (
		reasoning: string,
		reasoningSpanish: string | null | undefined
	) => {
		return reasoningSpanish || reasoning;
	};

	const calculateFinancials = (): {
		totalIncome: number;
		primaryIncome: number;
		secondaryIncome: number;
		partnerIncome: number;
		projectedPlatformIncome: number;
		totalExpenses: number;
		rent: number;
		mortgage: number;
		familyExpenses: number;
		creditPayment: number;
		fuelConsumption: number;
		electricCharge: number;
		gasolineAvailable: number;
		electricAvailable: number;
	} => {
		if (!questionnaire) {
			return {
				totalIncome: 0,
				primaryIncome: 0,
				secondaryIncome: 0,
				partnerIncome: 0,
				projectedPlatformIncome: 0,
				totalExpenses: 0,
				rent: 0,
				mortgage: 0,
				familyExpenses: 0,
				creditPayment: 0,
				fuelConsumption: 0,
				electricCharge: 0,
				gasolineAvailable: 0,
				electricAvailable: 0
			};
		}

		const primaryIncome = questionnaire.primary_income || 0;
		const secondaryIncome = questionnaire.secondary_income || 0;
		const partnerIncome = questionnaire.partner_income || 0;
		const projectedPlatformIncome = questionnaire.projected_platform_income || 0;
		const totalIncome = primaryIncome + secondaryIncome + partnerIncome + projectedPlatformIncome;

		const rent = questionnaire.rent || 0;
		const mortgage = questionnaire.mortgage_payment || 0;
		const familyExpenses = questionnaire.family_expenses || 0;
		const creditPayment = customerCreditPayment || 0;
		const totalExpenses = rent + mortgage + familyExpenses + creditPayment;

		const fuelConsumptionVariable = variables.find((v) => v.variable_name === 'fuel_consumption');
		const electricChargeVariable = variables.find((v) => v.variable_name === 'electric_charge');

		let fuelConsumption = fuelConsumptionVariable?.variable_value || 10500;
		let electricCharge = electricChargeVariable?.variable_value || 3000;

		const isPartTime = questionnaire.movility_platform_modality === 'part_time';
		if (isPartTime) {
			fuelConsumption = fuelConsumption / 2;
			electricCharge = electricCharge / 2;
		}

		const gasolineAvailable = Math.max(0, totalIncome - totalExpenses - fuelConsumption);
		const electricAvailable = Math.max(0, totalIncome - totalExpenses - electricCharge);

		return {
			totalIncome,
			primaryIncome,
			secondaryIncome,
			partnerIncome,
			projectedPlatformIncome,
			totalExpenses,
			rent,
			mortgage,
			familyExpenses,
			creditPayment,
			fuelConsumption,
			electricCharge,
			gasolineAvailable,
			electricAvailable
		};
	};

	const financials = calculateFinancials();
	const activeVehicles = vehicles
		.filter((v) => v.is_active)
		.sort((a, b) => {
			const priorityA = a.recommendation_priority ?? 0;
			const priorityB = b.recommendation_priority ?? 0;
			if (priorityA !== priorityB) return priorityA - priorityB;
			return a.minimum_weekly_payment - b.minimum_weekly_payment;
		});

	const paymentBufferVariable = variables.find((v) => v.variable_name === 'payment_buffer');
	const paymentBuffer = paymentBufferVariable?.variable_value || 0;

	if (!vehicleOffering) {
		return (
			<div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
				<div
					className="flex items-center justify-between cursor-pointer mb-4"
					onClick={() => setIsExpanded(!isExpanded)}
				>
					<h2 className="text-lg font-semibold">{t('VehicleOfferingInformation')}</h2>
					{isExpanded ? (
						<ChevronUp size={20} className="text-gray-500" />
					) : (
						<ChevronDown size={20} className="text-gray-500" />
					)}
				</div>
				{isExpanded && (
					<p className="text-gray-500 text-center py-6">{t('NoVehicleOfferingData')}</p>
				)}
			</div>
		);
	}

	return (
		<div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
			<div
				className="flex items-center justify-between cursor-pointer mb-4"
				onClick={() => setIsExpanded(!isExpanded)}
			>
				<h2 className="text-lg font-semibold">{t('VehicleOfferingInformation')}</h2>
				{isExpanded ? (
					<ChevronUp size={20} className="text-gray-500" />
				) : (
					<ChevronDown size={20} className="text-gray-500" />
				)}
			</div>
			{isExpanded && (
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<div>
						<label className="block text-sm text-gray-500 mb-1">
							{t('FuelPaymentAvailability')}
						</label>
						<p className="text-sm font-medium">
							{formatCurrency(vehicleOffering.fuel_payment_availability_before_credit)}
						</p>
					</div>
					<div>
						<label className="block text-sm text-gray-500 mb-1">
							{t('ElectricPaymentAvailability')}
						</label>
						<p className="text-sm font-medium">
							{formatCurrency(vehicleOffering.electric_payment_availability_before_credit)}
						</p>
					</div>
					<div>
						<label className="block text-sm text-gray-500 mb-1">{t('VehicleOfferingStatus')}</label>
						<p className="text-sm font-medium">
							{getVehicleOfferingLabel(vehicleOffering.vehicle_offering)}
						</p>
					</div>
					{vehicleOffering.vehicle_type && (
						<div>
							<label className="block text-sm text-gray-500 mb-1">{t('VehicleType')}</label>
							<p className="text-sm font-medium">
								{getVehicleTypeLabel(vehicleOffering.vehicle_type)}
							</p>
						</div>
					)}
					{vehicleOffering.vehicle_model && (
						<div>
							<label className="block text-sm text-gray-500 mb-1">{t('VehicleModel')}</label>
							<p className="text-sm font-medium">{vehicleOffering.vehicle_model}</p>
						</div>
					)}
					<div className="md:col-span-2">
						<label className="block text-sm text-gray-500 mb-1">{t('Reasoning')}</label>
						<p className="text-sm font-medium">
							{getVehicleOfferingReasoning(
								vehicleOffering.reasoning,
								vehicleOffering.reasoning_spanish || null
							)}
						</p>
					</div>
					<div>
						<label className="block text-sm text-gray-500 mb-1">{t('CreatedAt')}</label>
						<p className="text-sm font-medium">{formatDateTime(vehicleOffering.created_at)}</p>
					</div>
					<div>
						<label className="block text-sm text-gray-500 mb-1">{t('CreatedBy')}</label>
						<p className="text-sm font-medium">{vehicleOffering.created_by}</p>
					</div>

					{questionnaire && (
						<div className="md:col-span-2 mt-6">
							<h3 className="text-md font-semibold mb-4 pb-2 border-b border-gray-200">
								{t('FinancialBreakdown')}
							</h3>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
								<div className="border border-gray-200 rounded-lg p-4">
									<h4 className="text-sm font-semibold mb-3 text-gray-700">{t('TotalIncome')}</h4>
									<div className="space-y-2">
										<div className="flex justify-between text-sm">
											<span className="text-gray-600 italic">{t('PrimaryIncome')}</span>
											<span className="font-medium">
												{formatCurrency(financials.primaryIncome)}
											</span>
										</div>
										<div className="flex justify-between text-sm">
											<span className="text-gray-600 italic">{t('SecondaryIncome')}</span>
											<span className="font-medium">
												{formatCurrency(financials.secondaryIncome)}
											</span>
										</div>
										<div className="flex justify-between text-sm">
											<span className="text-gray-600 italic">{t('PartnerIncome')}</span>
											<span className="font-medium">
												{formatCurrency(financials.partnerIncome)}
											</span>
										</div>
										{financials.projectedPlatformIncome > 0 && (
											<div className="flex justify-between text-sm">
												<span className="text-gray-600 italic">{t('ProjectedPlatformIncome')}</span>
												<span className="font-medium">
													{formatCurrency(financials.projectedPlatformIncome)}
												</span>
											</div>
										)}
										<div className="flex justify-between text-sm pt-2 border-t border-gray-200 font-semibold">
											<span>{t('TotalIncome')}</span>
											<span>{formatCurrency(financials.totalIncome)}</span>
										</div>
									</div>
								</div>

								<div className="border border-gray-200 rounded-lg p-4">
									<h4 className="text-sm font-semibold mb-3 text-gray-700">{t('TotalExpenses')}</h4>
									<div className="space-y-2">
										<div className="flex justify-between text-sm">
											<span className="text-gray-600 italic">{t('Rent')}</span>
											<span className="font-medium">{formatCurrency(financials.rent)}</span>
										</div>
										<div className="flex justify-between text-sm">
											<span className="text-gray-600 italic">{t('Mortgage')}</span>
											<span className="font-medium">{formatCurrency(financials.mortgage)}</span>
										</div>
										<div className="flex justify-between text-sm">
											<span className="text-gray-600 italic">{t('FamilyExpenses')}</span>
											<span className="font-medium">
												{formatCurrency(financials.familyExpenses)}
											</span>
										</div>
										<div className="flex justify-between text-sm">
											<span className="text-gray-600 italic">{t('MonthlyPayment')}</span>
											<span className="font-medium">
												{formatCurrency(financials.creditPayment)}
											</span>
										</div>
										<div className="flex justify-between text-sm pt-2 border-t border-gray-200 font-semibold">
											<span>{t('TotalExpenses')}</span>
											<span>{formatCurrency(financials.totalExpenses)}</span>
										</div>
									</div>
								</div>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
								<div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
									<h4 className="text-sm font-semibold mb-3 text-blue-900">{t('Gasoline')}</h4>
									<div className="space-y-2">
										<div className="flex justify-between text-sm">
											<span className="text-blue-700">{t('TotalIncome')}</span>
											<span className="font-medium text-blue-900">
												+ {formatCurrency(financials.totalIncome)}
											</span>
										</div>
										<div className="flex justify-between text-sm">
											<span className="text-blue-700">{t('TotalExpenses')}</span>
											<span className="font-medium text-blue-900">
												- {formatCurrency(financials.totalExpenses)}
											</span>
										</div>
										<div className="flex justify-between text-sm">
											<span className="text-blue-700">{t('FuelCost')}</span>
											<span className="font-medium text-blue-900">
												- {formatCurrency(financials.fuelConsumption)}
											</span>
										</div>
										<div className="flex justify-between text-sm pt-2 border-t border-blue-200 font-semibold">
											<span className="text-blue-900">{t('AvailableBeforeCreditPayment')}</span>
											<span className="text-blue-900">
												{formatCurrency(financials.gasolineAvailable)}
											</span>
										</div>
									</div>
								</div>

								<div className="border border-green-200 bg-green-50 rounded-lg p-4">
									<h4 className="text-sm font-semibold mb-3 text-green-900">{t('Electric')}</h4>
									<div className="space-y-2">
										<div className="flex justify-between text-sm">
											<span className="text-green-700">{t('TotalIncome')}</span>
											<span className="font-medium text-green-900">
												+ {formatCurrency(financials.totalIncome)}
											</span>
										</div>
										<div className="flex justify-between text-sm">
											<span className="text-green-700">{t('TotalExpenses')}</span>
											<span className="font-medium text-green-900">
												- {formatCurrency(financials.totalExpenses)}
											</span>
										</div>
										<div className="flex justify-between text-sm">
											<span className="text-green-700">{t('FuelCost')}</span>
											<span className="font-medium text-green-900">
												- {formatCurrency(financials.electricCharge)}
											</span>
										</div>
										<div className="flex justify-between text-sm pt-2 border-t border-green-200 font-semibold">
											<span className="text-green-900">{t('AvailableBeforeCreditPayment')}</span>
											<span className="text-green-900">
												{formatCurrency(financials.electricAvailable)}
											</span>
										</div>
									</div>
								</div>
							</div>

							{activeVehicles.length > 0 && (
								<div className="mt-6">
									<h4 className="text-sm font-semibold mb-3">{t('VehicleAffordability')}</h4>
									<div className="border border-gray-200 rounded-lg overflow-hidden">
										<table className="w-full text-sm">
											<thead className="bg-gray-50">
												<tr>
													<th className="px-4 py-3 text-left font-semibold text-gray-700 border-b">
														{t('VehicleName')}
													</th>
													<th className="px-4 py-3 text-right font-semibold text-gray-700 border-b">
														{t('MonthlyPayment')}
													</th>
													<th className="px-4 py-3 text-center font-semibold text-gray-700 border-b">
														{t('CanAfford')}
													</th>
												</tr>
											</thead>
											<tbody>
												{activeVehicles.map((vehicle, index) => {
													const monthlyPayment = vehicle.minimum_weekly_payment * (52 / 12);
													const availableAmount =
														vehicle.fuel_type === 'fuel'
															? financials.gasolineAvailable
															: financials.electricAvailable;
													const canAfford = monthlyPayment <= availableAmount - paymentBuffer;

													return (
														<tr
															key={vehicle.id}
															className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
														>
															<td className="px-4 py-3 border-b">
																<div className="flex items-center gap-2">
																	<span className="text-gray-500 text-xs">
																		#{vehicle.recommendation_priority ?? 0}
																	</span>
																	<span>{vehicle.name}</span>
																	<span
																		className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
																			vehicle.fuel_type === 'electric'
																				? 'bg-green-100 text-green-700'
																				: 'bg-blue-100 text-blue-700'
																		}`}
																	>
																		{vehicle.fuel_type === 'electric'
																			? t('Electric')
																			: t('Gasoline')}
																	</span>
																</div>
															</td>
															<td className="px-4 py-3 text-right border-b">
																{formatCurrency(monthlyPayment)}
															</td>
															<td className="px-4 py-3 text-center border-b">
																<span
																	className={`inline-block px-3 py-1 rounded font-semibold ${
																		canAfford
																			? 'bg-green-100 text-green-800'
																			: 'bg-red-100 text-red-800'
																	}`}
																>
																	{canAfford ? t('Yes') : t('No')}
																</span>
															</td>
														</tr>
													);
												})}
											</tbody>
										</table>
									</div>
								</div>
							)}

							{activeVehicles.length === 0 && (
								<div className="mt-6">
									<p className="text-gray-500 text-center py-4 text-sm">
										{t('NoVehiclesAvailable')}
									</p>
								</div>
							)}
						</div>
					)}

					{!questionnaire && (
						<div className="md:col-span-2 mt-6">
							<div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
								<p className="text-gray-500 text-center text-sm">
									{t('NoQuestionnaireDataForBreakdown')}
								</p>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default VehicleOfferingSection;
